package dev.danko.roblox_craft_ai.shared.animation

/** How the character fights in an animation: no weapon, fists, one weapon or a weapon in each hand. */
enum class CombatMode { NONE, FISTS, ONE_WEAPON, TWO_WEAPONS }

/** Weapons the generator knows how to move with; heavier ones swing slower and follow through further. */
enum class WeaponKind { SWORD, GUN, STAFF, AXE }

/** Hold a single weapon with one hand only or with both hands on the handle. */
enum class Grip { ONE_HAND, TWO_HANDS }

/** The hand holding a single weapon (with a two-handed grip, the hand nearer the end of the handle). */
enum class Hand { RIGHT, LEFT }

/**
 * The choices of the animation settings panel, carried in the work's metadata and turned into instructions for
 * the motion generator.
 */
data class AnimationSetup(
    val mode: CombatMode = CombatMode.NONE,
    val weapon: WeaponKind = WeaponKind.SWORD,
    val offhand: WeaponKind = WeaponKind.SWORD,
    val grip: Grip = Grip.ONE_HAND,
    /** One of the user's 3D works held instead of the built-in weapon (storage URL), and its name. */
    val weaponModelUrl: String? = null,
    val weaponName: String? = null,
    /** With two weapons, the user's model in the left hand, and its name. */
    val offhandModelUrl: String? = null,
    val offhandName: String? = null,
    /** With one weapon, the hand holding it; the animation is made for the right hand and mirrored for the left. */
    val hand: Hand = Hand.RIGHT,
    /** A 3D work of the user's with a template skeleton (storage URL) playing the animation instead of the R15 figure. */
    val characterUrl: String? = null
) {
    fun toMetadata(): Map<String, String> = combatMetadata() + (characterUrl?.let { mapOf(CHARACTER to it) } ?: emptyMap())

    private fun combatMetadata(): Map<String, String> = if (mode == CombatMode.NONE) emptyMap() else buildMap {
        put(MODE, mode.name)
        if (mode != CombatMode.FISTS) put(WEAPON, weapon.name)
        if (mode == CombatMode.TWO_WEAPONS) put(OFFHAND, offhand.name)
        if (mode == CombatMode.ONE_WEAPON) put(GRIP, grip.name)
        if (mode == CombatMode.ONE_WEAPON && hand == Hand.LEFT) put(HAND, hand.name)
        if (mode != CombatMode.FISTS) {
            weaponModelUrl?.let { put(WEAPON_MODEL, it) }
            weaponName?.takeIf { weaponModelUrl != null }?.let { put(WEAPON_NAME, it.take(60)) }
        }
        if (mode == CombatMode.TWO_WEAPONS) {
            offhandModelUrl?.let { put(OFFHAND_MODEL, it) }
            offhandName?.takeIf { offhandModelUrl != null }?.let { put(OFFHAND_NAME, it.take(60)) }
        }
    }

    /** The joint that holds the single weapon (or, with two, the right one). */
    val mainHandJoint: String get() = if (mode == CombatMode.ONE_WEAPON && hand == Hand.LEFT) "LeftHand" else "RightHand"

    /** Instructions for the motion generator, or null without a combat setup. */
    fun instructions(): String? = when (mode) {
        CombatMode.NONE -> null
        CombatMode.FISTS -> "Unarmed fighting stance: hands closed in fists, guard up in front of the face, knees soft. " +
            "Strikes start from the hips and shoulders and snap back to the guard; dodges shift the weight to one leg."
        // A left-handed animation is made for the right hand and mirrored afterwards.
        CombatMode.ONE_WEAPON -> "The character holds ${named(weaponName, weapon)} in the right hand (RightHand). " +
            (if (grip == Grip.TWO_HANDS) "Two-handed grip: the left hand stays on the handle right next to the right hand all the time. "
            else "One-handed grip: the left arm balances the body. ") + weapon.motion()
        CombatMode.TWO_WEAPONS -> "The character holds ${named(weaponName, weapon)} in the right hand and ${named(offhandName, offhand)} in the left hand. " +
            "Alternate the hands in combos, the free hand guards while the other strikes. " + weapon.motion() +
            (if (offhand != weapon) " " + offhand.motion() else "")
    }

    companion object {
        const val MODE = "animMode"
        const val WEAPON = "animWeapon"
        const val OFFHAND = "animOffhand"
        const val GRIP = "animGrip"
        const val WEAPON_MODEL = "animWeaponModel"
        const val CHARACTER = "animCharacter"
        const val WEAPON_NAME = "animWeaponName"
        const val OFFHAND_MODEL = "animOffhandModel"
        const val OFFHAND_NAME = "animOffhandName"
        const val HAND = "animHand"

        /**
         * How a weapon of the user's moves, guessed from its name: something aimed, something long, something heavy,
         * otherwise a light blade.
         */
        fun guessKind(name: String): WeaponKind {
            val words = buildList {
                val word = StringBuilder()
                for (char in name.lowercase() + " ") {
                    if (char.isLetter()) word.append(char) else if (word.isNotEmpty()) { add(word.toString()); word.clear() }
                }
            }
            // Stems of four letters or more match the start of a word ("ружь" in "ружьё"); shorter ones a whole word
            // or its plural, so "bat" is not found in "combat" nor "bow" in "elbow".
            fun has(vararg stems: String) = words.any { word ->
                stems.any { stem -> if (stem.length >= 4) word.startsWith(stem) else word == stem || word == stem + "s" }
            }
            return when {
                has("gun", "pistol", "rifle", "blaster", "revolver", "shotgun", "bow", "crossbow", "пистолет", "ружь", "автомат",
                    "бластер", "револьвер", "винтовк", "дробовик", "пушк", "лук", "лука", "луком", "арбалет", "gewehr", "pistole") -> WeaponKind.GUN
                has("staff", "spear", "lance", "wand", "pole", "halberd", "trident", "scythe", "посох", "копь", "жезл", "шест",
                    "алебард", "трезуб", "коса", "speer", "stab") -> WeaponKind.STAFF
                has("axe", "hammer", "mace", "club", "bat", "maul", "pickaxe", "sledge", "топор", "молот", "булав", "дубин", "бита", "биту", "битой",
                    "кувалд", "кирк", "keule", "axt") -> WeaponKind.AXE
                else -> WeaponKind.SWORD
            }
        }

        private fun named(name: String?, kind: WeaponKind): String =
            name?.trim()?.takeIf { it.isNotEmpty() }?.let { "\"${it.take(60).replace("\"", "'")}\" (moves like ${kind.describe()})" } ?: kind.describe()

        fun fromMetadata(metadata: Map<String, String>): AnimationSetup = AnimationSetup(
            mode = metadata[MODE]?.let { v -> CombatMode.entries.firstOrNull { it.name == v } } ?: CombatMode.NONE,
            weapon = metadata[WEAPON]?.let { v -> WeaponKind.entries.firstOrNull { it.name == v } } ?: WeaponKind.SWORD,
            offhand = metadata[OFFHAND]?.let { v -> WeaponKind.entries.firstOrNull { it.name == v } } ?: WeaponKind.SWORD,
            grip = metadata[GRIP]?.let { v -> Grip.entries.firstOrNull { it.name == v } } ?: Grip.ONE_HAND,
            weaponModelUrl = metadata[WEAPON_MODEL]?.takeIf { it.startsWith("/api/v1/storage/") },
            weaponName = metadata[WEAPON_NAME],
            offhandModelUrl = metadata[OFFHAND_MODEL]?.takeIf { it.startsWith("/api/v1/storage/") },
            offhandName = metadata[OFFHAND_NAME],
            hand = metadata[HAND]?.let { v -> Hand.entries.firstOrNull { it.name == v } } ?: Hand.RIGHT,
            characterUrl = metadata[CHARACTER]?.takeIf { it.startsWith("/api/v1/storage/") }
        )

        private fun WeaponKind.describe() = when (this) {
            WeaponKind.SWORD -> "a sword"
            WeaponKind.GUN -> "a gun"
            WeaponKind.STAFF -> "a long staff"
            WeaponKind.AXE -> "a heavy axe"
        }

        private fun WeaponKind.motion() = when (this) {
            WeaponKind.SWORD -> "A sword is light: quick wind-up, a fast arc, a short follow-through, then back to the guard."
            WeaponKind.GUN -> "A gun is aimed with the arm straight forward at shoulder height (RightUpperArm x about 90); each shot kicks the hand up a little and it settles back."
            WeaponKind.STAFF -> "A staff is long: wide sweeps and thrusts driven by the torso twist, spins around the body."
            WeaponKind.AXE -> "An axe is heavy: a slow wind-up high over the shoulder, the whole body leans into the swing, a long follow-through and a slow recovery. " +
                "Keep the implied bat/axe path outside the body with a visible offset from the head and torso; widen the shoulder and elbow arc instead of letting the weapon cut through the chest."
        }
    }
}
